/**
 * Play the agent's hold-confirmation sound. If a URL is configured (the
 * uploaded music-on-hold file), play it best-effort; otherwise synthesize a
 * short, subtle two-tone blip with the Web Audio API so no asset is bundled.
 * Never throws — a blocked autoplay or missing AudioContext is swallowed.
 */
export function playHoldSound(url?: string | null): void {
  if (url) {
    try {
      const audio = new Audio(url);
      audio.volume = 0.4;
      void audio.play().catch(() => synthBlip());
      return;
    } catch {
      // fall through to the synthesized blip
    }
  }
  synthBlip();
}

/** ~200ms descending two-tone blip (880Hz → 660Hz), quiet. */
export function synthBlip(): void {
  try {
    const Ctx = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const now = ctx.currentTime;
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(0.15, now + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.2);
    gain.connect(ctx.destination);

    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(880, now);
    osc.frequency.setValueAtTime(660, now + 0.1);
    osc.connect(gain);
    osc.start(now);
    osc.stop(now + 0.22);
    osc.onended = () => { try { void ctx.close(); } catch { /* ignore */ } };
  } catch {
    // no audio available — silent
  }
}
